// Geometry of the locked editor's grid: one drum lane (16 pads), then each instrument's lane and
// the extra lanes the song added for it (its alternates), whose piano rolls show only the scale's
// notes (SPEC §3.4), then the atmos kit's lane (12 pads). Pure; used for drawing and hit tests.
import {
  NOTE_NAMES,
  FIRST_ATMOS,
  NUM_ATMOS_PADS,
  NUM_SOUNDS,
  pitchClass,
  ROLE_RANGE,
  scaleNotesInRange,
  SLOT_ROLE,
  type InstrumentRole,
  type Key,
} from "@eb/shared";

/** "drums", an instrument ("bass" …), or an extra lane ("lane-20" … "lane-27"). */
export type LaneId = string;
/** What a lane plays (its colour and name). */
export type LaneRole = "drums" | InstrumentRole | "atmos";

export type GridRow =
  | { kind: "header"; lane: LaneId; role: LaneRole; y: number; h: number; slot: number }
  | { kind: "pad"; lane: "drums" | "atmos"; role: "drums" | "atmos"; slot: number; y: number; h: number }
  | {
      kind: "note";
      lane: LaneId;
      role: InstrumentRole;
      slot: number;
      note: number;
      y: number;
      h: number;
      tonic: boolean;
    };

export const HEADER_H = 30;
export const PAD_ROW_H = 22;
export const NOTE_ROW_H = 18;
export const LABEL_W = 132;

export const LANES: { lane: LaneId; slot: number }[] = [
  { lane: "drums", slot: 0 },
  { lane: "bass", slot: 16 },
  { lane: "harmony", slot: 17 },
  { lane: "lead", slot: 18 },
  { lane: "classic", slot: 19 },
  { lane: "atmos", slot: FIRST_ATMOS },
];

/** The grid's lanes: drums, then each instrument followed by its extra lanes (sounds 20–27). */
export function gridLanes(extra: readonly number[] = []): { lane: LaneId; role: LaneRole; slot: number }[] {
  return LANES.flatMap(({ lane, slot }) => [
    { lane, role: (lane === "drums" || lane === "atmos" ? lane : SLOT_ROLE[slot]) as LaneRole, slot },
    ...[...extra]
      .filter((p) => slot >= 16 && slot < FIRST_ATMOS && SLOT_ROLE[p] === SLOT_ROLE[slot] && p >= NUM_SOUNDS)
      .sort((a, b) => a - b)
      .map((p) => ({ lane: `lane-${p}`, role: SLOT_ROLE[p] as LaneRole, slot: p })),
  ]);
}

export function buildRows(
  key: Key,
  collapsed: ReadonlySet<LaneId> = new Set(),
  extra: readonly number[] = [],
): { rows: GridRow[]; height: number } {
  const rows: GridRow[] = [];
  let y = 0;
  for (const { lane, role, slot } of gridLanes(extra)) {
    rows.push({ kind: "header", lane, role, slot, y, h: HEADER_H });
    y += HEADER_H;
    if (collapsed.has(lane)) continue;
    if (role === "drums" || role === "atmos") {
      // Drum pads (0–15) or the atmos kit's pads (20–31): one row each.
      const first = role === "drums" ? 0 : FIRST_ATMOS;
      for (let i = 0; i < (role === "drums" ? 16 : NUM_ATMOS_PADS); i++) {
        rows.push({ kind: "pad", lane: role, role, slot: first + i, y, h: PAD_ROW_H });
        y += PAD_ROW_H;
      }
    } else {
      const r = ROLE_RANGE[role];
      // Highest note on top, like a piano roll; only in-key notes exist.
      for (const note of scaleNotesInRange(key, r.lo, r.hi).reverse()) {
        rows.push({
          kind: "note",
          lane,
          role,
          slot,
          note,
          y,
          h: NOTE_ROW_H,
          tonic: pitchClass(note) === pitchClass(key.tonic),
        });
        y += NOTE_ROW_H;
      }
    }
  }
  return { rows, height: y };
}

/** Index of the row at y (binary search), or −1. */
export function rowAt(rows: GridRow[], y: number): number {
  let lo = 0;
  let hi = rows.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const r = rows[mid];
    if (y < r.y) hi = mid - 1;
    else if (y >= r.y + r.h) lo = mid + 1;
    else return mid;
  }
  return -1;
}

/** The row a note event is drawn in. */
export function rowOfEvent(rows: GridRow[], pad: number, note: number): number {
  return rows.findIndex(
    (r) => (r.kind === "pad" && r.slot === pad) || (r.kind === "note" && r.slot === pad && r.note === note),
  );
}

export interface View {
  /** Tick at the left edge of the note area. */
  startTick: number;
  ticksPerPx: number;
}

export const tickToX = (tick: number, v: View) => LABEL_W + (tick - v.startTick) / v.ticksPerPx;
export const xToTick = (x: number, v: View) => v.startTick + (x - LABEL_W) * v.ticksPerPx;

/** Grid cell start for x (floor to the grid). */
export function cellTick(x: number, v: View, grid: number): number {
  return Math.floor(xToTick(x, v) / grid) * grid;
}

export function noteLabel(note: number): string {
  return `${NOTE_NAMES[pitchClass(note)]}${Math.floor(note / 12) - 1}`;
}

/** Drag gesture: the first clear movement picks velocity (vertical) or length (horizontal). */
export function dragMode(dx: number, dy: number, alt: boolean): "velocity" | "length" | null {
  if (Math.hypot(dx, dy) < 3) return null;
  return alt || Math.abs(dx) > Math.abs(dy) ? "length" : "velocity";
}

export function dragVelocity(startVel: number, dy: number): number {
  return Math.round(Math.min(127, Math.max(1, startVel - dy * 0.9)));
}

/** New length while dragging: snaps to a quarter of the grid (Shift: single ticks). */
export function dragLength(startLen: number, dx: number, ticksPerPx: number, grid: number, fine: boolean): number {
  const snap = fine ? 1 : grid / 4;
  return Math.max(snap, Math.round((startLen + dx * ticksPerPx) / snap) * snap);
}
